import { api, ApiError } from './api';

export interface PasskeyInfo { credential_id: string; created_at: string; last_used_at: string | null }
export type RegistrationStage = 'library' | 'options' | 'authenticator' | 'verification' | 'list_refresh';
export type RegistrationResult = 'ready' | 'cancelled' | 'failed' | 'unconfirmed';
// UI-only context for reconciling a lost verification response. Never logged or persisted locally.
const attempts = new WeakMap<object, { stage: RegistrationStage; credentialId?: string }>();
export function rememberRegistrationAttempt(error: unknown, stage: RegistrationStage, credentialId?: string) {
  if (error && typeof error === 'object') attempts.set(error, { stage, credentialId });
}
export function registrationMessage(result: RegistrationResult, zh: boolean) {
  const messages = {
    ready: ['Quick sign-in is ready.', '快速登入已設定。'],
    cancelled: ['Quick sign-in setup was cancelled.', '已取消快速登入設定。'],
    failed: ['Set up was not completed. Your existing session and email sign-in are unchanged.', '未能完成設定。現有登入及電郵登入不受影響。'],
    unconfirmed: ['Could not confirm quick sign-in setup. Reload to check your saved passkeys. Email sign-in remains available.', '暫時未能確認快速登入設定。請重新載入查看已儲存的快速登入；你仍可使用電郵登入。'],
  };
  return messages[result][zh ? 1 : 0];
}
export async function reconcileRegistrationFailure(error: unknown, load = loadPasskeys): Promise<{ result: RegistrationResult; keys?: PasskeyInfo[] }> {
  const detail = registrationDiagnostic('verification', error);
  const attempt = error && typeof error === 'object' ? attempts.get(error) : undefined;
  const duplicate = detail.code === 'PASSKEY_DUPLICATE_CREDENTIAL' || detail.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED'
    || detail.errorName === 'InvalidStateError' || detail.browserErrorName === 'InvalidStateError';
  if (!duplicate && (detail.errorName === 'NotAllowedError' || detail.browserErrorName === 'NotAllowedError'
    || detail.errorName === 'AbortError' || detail.code === 'ERROR_CEREMONY_ABORTED')) return { result: 'cancelled' };
  // A server-declared rejection is not overridden by an unrelated, previously saved passkey.
  if (!duplicate && error instanceof ApiError && ['PASSKEY_VERIFICATION_FAILED','PASSKEY_PERSISTENCE_FAILED','PASSKEY_CHALLENGE_REJECTED','PASSKEY_OPTIONS_FAILED'].includes(error.code || '')) {
    return { result: 'failed' };
  }
  if (duplicate || attempt?.credentialId) {
    try {
      const keys = await load();
      const persisted = attempt?.credentialId ? keys.some(key => key.credential_id === attempt.credentialId) : duplicate && keys.length > 0;
      return { result: persisted ? 'ready' : duplicate ? 'failed' : 'unconfirmed', keys };
    } catch { return { result: 'unconfirmed' }; }
  }
  return { result: attempt?.stage === 'verification' ? 'unconfirmed' : 'failed' };
}

// Do not log error.message/cause, requests, attestation, challenge, user or credential IDs.
export function registrationDiagnostic(stage: RegistrationStage, error: unknown) {
  const value = error && typeof error === 'object' ? error as { name?: unknown; code?: unknown; cause?: { name?: unknown } } : {};
  const names = ['NotAllowedError', 'NotSupportedError', 'SecurityError', 'InvalidStateError', 'AbortError', 'ConstraintError', 'TypeError', 'UnknownError', 'Error'];
  const codes = ['ERROR_CEREMONY_ABORTED', 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED', 'ERROR_PASSTHROUGH_SEE_CAUSE_PROPERTY',
    'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT', 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT',
    'ERROR_MALFORMED_PUBKEYCREDPARAMS', 'ERROR_AUTHENTICATOR_NO_SUPPORTED_PUBKEYCREDPARAMS_ALG', 'ERROR_INVALID_RP_ID', 'ERROR_INVALID_DOMAIN',
    'PASSKEY_CHALLENGE_REJECTED', 'PASSKEY_VERIFICATION_FAILED', 'PASSKEY_DUPLICATE_CREDENTIAL', 'PASSKEY_PERSISTENCE_FAILED', 'PASSKEY_OPTIONS_FAILED'];
  return { event: 'passkey_registration', stage,
    errorName: names.includes(String(value.name)) ? String(value.name) : 'UnknownError',
    browserErrorName: names.includes(String(value.cause?.name)) ? String(value.cause?.name) : undefined,
    code: codes.includes(String(value.code)) ? String(value.code) : undefined,
    status: error instanceof ApiError ? error.status : undefined };
}
export function logRegistrationFailure(stage: RegistrationStage, error: unknown) {
  console.warn(registrationDiagnostic(stage, error));
}
export async function loadPasskeys() {
  const data = await api<{ passkeys: PasskeyInfo[] }>('/api/student/passkeys');
  return data.passkeys;
}

// A successful credential write must never be reported as failed because a later list GET failed.
export async function completeRegistration(register: () => Promise<void>, refresh: () => Promise<void>, onRegistered: () => void = () => {}) {
  await register();
  onRegistered();
  try { await refresh(); return 'registered' as const; }
  catch (error) { logRegistrationFailure('list_refresh', error); return 'registered_list_unavailable' as const; }
}
